import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage'

import { Confirm } from './../../../Commons/Modals/Confirm'


/**
 * ==============================================================================
 * ESTILOS INLINE
 * ==============================================================================
 */
const styles = {
  container: { display: "flex", flexDirection: "column", gap: 20 },
  row: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  card: { background: "#fff", border: "1px solid #ddd", borderRadius: 8, padding: 16, boxShadow: "0 2px 5px rgba(0,0,0,0.05)" },
  label: { fontWeight: 700, fontSize: "0.85rem", color: "#555", display: "block", marginBottom: 4 },
  input: { padding: 8, borderRadius: 4, border: "1px solid #ccc", width: "100%" },
  textarea: { padding: 10, borderRadius: 4, border: "1px solid #ccc", width: "100%", minHeight: "200px", fontFamily: "monospace", fontSize: "0.9rem" },
  btn: { padding: "8px 16px", borderRadius: 4, border: "none", background: "#eee", cursor: "pointer", fontWeight: 600 },
  btnPrimary: { background: "#007bff", color: "#fff" },
  btnDanger: { background: "#dc3545", color: "#fff" },
  blockList: { display: "flex", flexDirection: "column", gap: 8, maxHeight: "500px", overflowY: "auto", border: "1px solid #eee", padding: 8, borderRadius: 4 },
  blockItem: { display: "flex", alignItems: "center", gap: 10, background: "#f9f9f9", border: "1px solid #eee", padding: 8, borderRadius: 4, cursor: "pointer" },
  activeBlock: { borderLeft: "4px solid #007bff", background: "#f0f8ff" },
  tapBtn: { minWidth: "80px", border: "1px solid #bbb", fontWeight: "bold" },
  statusBadge: { fontSize: '0.75rem', padding: '2px 6px', borderRadius: '4px', marginLeft: '10px', fontWeight: 'bold' },
  // Estilos para el nuevo modal interno (Overlay simple)
  modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1100 },
  modalContent: { background: "#fff", padding: 20, borderRadius: 8, width: "600px", maxWidth: "90%", display: "flex", flexDirection: "column", gap: 15, boxShadow: "0 4px 15px rgba(0,0,0,0.3)" }
}

/**
 * ==============================================================================
 * COMPONENTE: KaraokeSongCreator
 * ==============================================================================
 */
const KaraokeSongCreator = ({ songs, selectedSongId, onSongsChanged, onRequestClose, generateUuid }) => {
  
  // -- ESTADOS GLOBALES --
  const [draft, setDraft] = useState({ id: "", name: "", artist: "", url: "", lyrics: [] })
  const [originalState, setOriginalState] = useState(null)
  const [isNewSong, setIsNewSong] = useState(false)
  const [editingBlockId, setEditingBlockId] = useState(null)

  // -- ESTADOS MODAL CONFIRMACIÓN --
  const [showConfirm, setShowConfirm] = useState(false)
  const [confirmConfig, setConfirmConfig] = useState(null)

  // -- ESTADOS MODAL IMPORTAR TEXTO (NUEVO) --
  const [showImportTextModal, setShowImportTextModal] = useState(false)
  const [textToImport, setTextToImport] = useState("")

  // -- ESTADO FORMULARIO BLOQUE --
  const [blockForm, setBlockForm] = useState({
    type: "lyric", mode: "uniform", text: "", durationMs: 3000, display: "icon", parts: [],
  })

  // -- ESTADO HERRAMIENTAS TAP --
  const [isTiming, setIsTiming] = useState(false)
  const [timingStart, setTimingStart] = useState(0)
  const [measuringPartIndex, setMeasuringPartIndex] = useState(null)

  const { showMessage } = useFloatMessage()

  /** CARGA INICIAL */
  useEffect(() => {
    const current = songs.find((s) => s.id === selectedSongId)
    if (current) {
      const data = JSON.parse(JSON.stringify(current))
      setDraft(data)
      setOriginalState(JSON.stringify(data))
      setIsNewSong(false)
    } else {
      createNewDraft()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSongId, songs])

  /** DETECCIÓN DE CAMBIOS */
  const hasUnsavedChanges = useMemo(() => {
      return JSON.stringify(draft) !== originalState
  }, [draft, originalState])

  // -- GESTIÓN DEL DRAFT --
  const createNewDraft = () => {
    const newS = { id: generateUuid(), name: "Nueva Canción", artist: "Artista Desconocido", url: "", lyrics: [], createdAt: new Date().toISOString() }
    setDraft(newS)
    setOriginalState(JSON.stringify(newS))
    setIsNewSong(true)
    setEditingBlockId(null)
  }

  const updateDraftField = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }))

  // -- ACCIONES PRINCIPALES --

  const requestClean = () => {
      setConfirmConfig({
          message: "¿Estás seguro de limpiar el editor? Se perderán los cambios no guardados.",
          textYesButton: "Sí, limpiar",
          textNoButton: "Cancelar",
          actionToYesButton: async () => {
              createNewDraft()
              return { success: true }
          }
      })
      setShowConfirm(true)
  }

  const requestDuplicate = () => {
      setConfirmConfig({
          message: `¿Duplicar la canción "${draft.name}"?`,
          textYesButton: "Duplicar",
          textNoButton: "Cancelar",
          actionToYesButton: async () => {
              const copy = { ...draft, id: generateUuid(), name: `${draft.name} (Copia)`, createdAt: new Date().toISOString() }
              onSongsChanged(copy, copy.id)
              return { success: true }
          }
      })
      setShowConfirm(true)
  }

  const handleSaveSong = async () => {
    if (!draft.name.trim()) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'La canción necesita un nombre' })
    
    const now = new Date().toISOString()
    const songToSave = { ...draft, updatedAt: now }

    setOriginalState(JSON.stringify(songToSave))
    onSongsChanged(songToSave, songToSave.id)
  }

  const handleDeleteSong = () => {
    if (!window.confirm("¿Borrar canción? Esta acción no se puede deshacer.")) return
    const nextSongs = songs.filter((s) => s.id !== draft.id)
    onSongsChanged(nextSongs, null)
  }

  // -- IMPORT / EXPORT JSON --
  const copyJson = () => {
      navigator.clipboard.writeText(JSON.stringify(draft, null, 2))
        .then(() => showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'JSON copiado al portapapeles' }))
  }

  const importJson = async () => {
    try {
      const text = await navigator.clipboard.readText()
      const obj = JSON.parse(text)
      if (!Array.isArray(obj.lyrics)) throw new Error()
      
      const imported = { ...obj, id: generateUuid(), name: obj.name + " (Importada)" }
      setDraft(imported)
      showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Canción importada (Recuerda guardar)' })
    } catch {
      showMessage({ type: MESSAGE_TYPE.BAD, text: 'JSON inválido' })
    }
  }

  /**
   * NUEVO: LÓGICA DE IMPORTACIÓN DE TEXTO PLANO
   * Convierte texto crudo en bloques estructurados.
   */
  const processImportedText = () => {
      if (!textToImport.trim()) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Escribe algo de texto para importar' })

      const lines = textToImport.split(/\r?\n/) // Soporte para saltos de línea Windows/Unix
      const newBlocks = []

      lines.forEach(rawLine => {
          const line = rawLine.trim()
          
          // Caso 0: Línea vacía -> Ignorar (como solicitaste)
          if (!line) return 

          // Caso 1: Instrumental (#)
          if (line.startsWith('#')) {
              newBlocks.push({
                  id: generateUuid(),
                  type: 'instrumental',
                  display: 'icon',
                  mode: 'uniform',
                  durationMs: 3000, // Tiempo default
                  parts: []
              })
              return
          }

          // Caso 2: Bloque por Partes/Sílabas (*)
          if (line.includes('*')) {
              // Separamos por * y filtramos vacíos
              const partsRaw = line.split('*').map(p => p.trim()).filter(p => p !== '')
              
              if (partsRaw.length > 0) {
                  const partsObj = partsRaw.map(textPart => ({
                      text: textPart,
                      durationMs: 1000 // Tiempo default por parte
                  }))
                  
                  newBlocks.push({
                      id: generateUuid(),
                      type: 'lyric',
                      mode: 'parts',
                      text: '', // No se usa en modo parts
                      durationMs: partsObj.length * 1000,
                      parts: partsObj
                  })
              }
              return
          }

          // Caso 3: Bloque Uniforme (Texto normal + Enter)
          newBlocks.push({
              id: generateUuid(),
              type: 'lyric',
              mode: 'uniform',
              text: line,
              durationMs: 3000, // Tiempo default
              parts: []
          })
      })

      // Agregamos los bloques al final del borrador actual
      setDraft(prev => ({
          ...prev,
          lyrics: [...prev.lyrics, ...newBlocks]
      }))

      showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `${newBlocks.length} bloques generados correctamente` })
      setTextToImport("")
      setShowImportTextModal(false)
  }

  // -- GESTIÓN DE BLOQUES (CRUD INTERNO) --
  const resetBlockForm = () => {
    setBlockForm({ type: "lyric", mode: "uniform", text: "", durationMs: 3000, display: "icon", parts: [] })
    setEditingBlockId(null)
  }

  const loadBlockToEdit = (block) => {
    setBlockForm({
      type: block.type || "lyric",
      mode: block.mode || "uniform",
      text: block.text || "",
      durationMs: block.durationMs || 2000,
      display: block.display || "icon",
      parts: block.parts ? JSON.parse(JSON.stringify(block.parts)) : [],
    })
    setEditingBlockId(block.id)
  }

  const saveBlock = () => {
    const newBlock = { id: editingBlockId || generateUuid(), type: blockForm.type, durationMs: Number(blockForm.durationMs) }

    if (blockForm.type === "lyric") {
      newBlock.mode = blockForm.mode
      if (blockForm.mode === "uniform") {
        newBlock.text = blockForm.text
      } else {
        if (blockForm.parts.length === 0) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Debes agregar al menos una parte' })
        const totalPartsMs = blockForm.parts.reduce((acc, p) => acc + (Number(p.durationMs) || 0), 0)
        newBlock.parts = blockForm.parts
        newBlock.durationMs = totalPartsMs
      }
    } else {
      newBlock.display = blockForm.display
    }

    let newLyrics = [...draft.lyrics]
    if (editingBlockId) {
      const idx = newLyrics.findIndex((b) => b.id === editingBlockId)
      if (idx >= 0) newLyrics[idx] = newBlock
    } else {
      newLyrics.push(newBlock)
    }

    setDraft((prev) => ({ ...prev, lyrics: newLyrics }))
    
    showMessage({ 
        type: MESSAGE_TYPE.SUCCESS, 
        text: editingBlockId ? 'Bloque actualizado correctamente' : 'Bloque agregado correctamente' 
    })

    resetBlockForm()
  }

  const removeBlock = (blockId) => {
    setDraft((prev) => ({ ...prev, lyrics: prev.lyrics.filter((b) => b.id !== blockId) }))
    if (editingBlockId === blockId) resetBlockForm()
  }

  const moveBlock = (index, direction) => {
    const newLyrics = [...draft.lyrics]
    if (direction === -1 && index > 0) [newLyrics[index], newLyrics[index - 1]] = [newLyrics[index - 1], newLyrics[index]]
    else if (direction === 1 && index < newLyrics.length - 1) [newLyrics[index], newLyrics[index + 1]] = [newLyrics[index + 1], newLyrics[index]]
    setDraft((prev) => ({ ...prev, lyrics: newLyrics }))
  }

  // -- HERRAMIENTAS TAP --
  const toggleTiming = () => {
    if (!isTiming) {
      setIsTiming(true)
      setTimingStart(performance.now())
    } else {
      const diff = Math.round(performance.now() - timingStart)
      setIsTiming(false)
      setBlockForm(prev => ({ ...prev, durationMs: diff }))
    }
  }

  const startMeasuringPart = (index) => {
    setMeasuringPartIndex(index)
    setTimingStart(performance.now())
  }

  const stopMeasuringPart = (index) => {
    if (measuringPartIndex !== index) return
    const diff = Math.round(performance.now() - timingStart)
    const newParts = [...blockForm.parts]
    newParts[index].durationMs = diff
    setBlockForm(prev => ({ ...prev, parts: newParts }))
    setMeasuringPartIndex(null)
  }

  // -- RENDER HELPERS --
  const renderBlockItem = (block, index) => {
    let title = block.type === "instrumental" ? `🎵 INSTRUMENTAL (${block.display})` : (block.mode === "uniform" ? block.text : `[PARTS] ${block.parts?.map(p => p.text).join("")}`)
    const isEditing = editingBlockId === block.id

    return (
      <div key={block.id} style={{ ...styles.blockItem, ...(isEditing ? styles.activeBlock : {}) }}>
        <div style={{ fontWeight: "bold", color: "#888", width: 20 }}>{index + 1}.</div>
        <div style={{ flex: 1, overflow: "hidden" }} onClick={() => loadBlockToEdit(block)}>
          <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title || "(Vacío)"}</div>
          <div style={{ fontSize: 11, color: "#666" }}>{block.durationMs}ms</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <button onClick={() => moveBlock(index, -1)} style={{ fontSize: 10, cursor: "pointer", border: "none", background: "none" }}>▲</button>
          <button onClick={() => moveBlock(index, 1)} style={{ fontSize: 10, cursor: "pointer", border: "none", background: "none" }}>▼</button>
        </div>
        <button onClick={(e) => { e.stopPropagation(); removeBlock(block.id); }} style={{ ...styles.btn, ...styles.btnDanger, padding: "2px 6px", fontSize: 10 }}>X</button>
      </div>
    )
  }

  return (
    <div style={styles.container}>
        {/* CABECERA */}
        <div className='LyricsGenerator__Editor__MetadataSong Element--Shadow'>
            <div className='LyricsGenerator__Editor__MetadataSong__Header'>
                <div style={{display:'flex', alignItems:'center', flexWrap:'wrap', gap: 10}}>
                    {isNewSong ? (
                        <span className='LyricsGenerator__Text--Title'><i className='bi bi-plus-circle'></i> Nueva canción</span>
                    ) : (
                        <span className='LyricsGenerator__Text--Title'><i className='bi bi-pencil'></i> Editando canción</span>
                    )}

                    {/* INDICADORES DE ESTADO */}
                    {hasUnsavedChanges && (
                        <span style={{...styles.statusBadge, background:'#fff3cd', color:'#856404', border:'1px solid #ffeeba'}}>
                            <i className="bi bi-exclamation-triangle-fill"></i> Cambios sin guardar
                        </span>
                    )}
                    {!hasUnsavedChanges && draft.updatedAt && (
                        <span style={{...styles.statusBadge, color:'#888', fontWeight:'normal'}}>
                            Guardado: {new Date(draft.updatedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </span>
                    )}
                </div>

                <div className='LyricsGenerator__Editor__MetadataSong__Buttons'>
                    <button className='Btn__Adsl--Red LyricsGenerator__Editor__MetadataSong__Buttons__Button' onClick={handleSaveSong}><i className='bi bi-floppy'></i> Guardar</button>
                    {/* Botones con modal de confirmación */}
                    <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Editor__MetadataSong__Buttons__Button' onClick={requestDuplicate}><i className='bi bi-files'></i> Duplicar</button>
                    <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Editor__MetadataSong__Buttons__Button' onClick={requestClean}><i className='bi bi-eraser'></i> Limpiar</button>
                </div>
            </div>

            <div style={styles.row}>
                <div style={{ flex: 1 }}><label style={styles.label}>Nombre</label><input style={styles.input} value={draft.name} onChange={(e) => updateDraftField("name", e.target.value)} /></div>
                <div style={{ flex: 1 }}><label style={styles.label}>Artista</label><input style={styles.input} value={draft.artist} onChange={(e) => updateDraftField("artist", e.target.value)} /></div>
                <div style={{ flex: 1 }}><label style={styles.label}>URL</label><input style={styles.input} value={draft.url} onChange={(e) => updateDraftField("url", e.target.value)} /></div>
            </div>

            {/* BARRA DE UTILIDADES */}
            <div style={{ marginTop: 10, display: "flex", gap: 15, alignItems: "center" }}>
                <button onClick={() => setShowImportTextModal(true)} style={{ fontSize: 12, background: "none", border: "none", color: "#007bff", cursor: "pointer", textDecoration: "underline", fontWeight: "bold" }}><i className='bi bi-file-earmark-text'></i> Importar Letra (Texto)</button>
                <div style={{width: 1, height: 15, background: "#ccc"}}></div>
                <button onClick={copyJson} style={{ fontSize: 12, background: "none", border: "none", color: "#666", cursor: "pointer", textDecoration: "underline" }}><i className='bi bi-copy'></i> Copiar JSON</button>
                <button onClick={importJson} style={{ fontSize: 12, background: "none", border: "none", color: "#666", cursor: "pointer", textDecoration: "underline" }}><i className='bi bi-clipboard2'></i> Pegar JSON</button>
                
                {!isNewSong && (
                    <button onClick={handleDeleteSong} style={{ fontSize: 12, background: "none", border: "none", color: "red", cursor: "pointer", marginLeft: "auto", textDecoration: "underline" }}><i className='bi bi-trash'></i> Eliminar</button>
                )}
            </div>
      </div>

      {/* WORKSPACE */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* LISTA IZQ */}
        <div>
          <label style={{ ...styles.label, marginBottom: 8 }}>Línea de Tiempo ({draft.lyrics.length} bloques)</label>
          <div style={styles.blockList}>
            {draft.lyrics.length === 0 ? <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Sin bloques</div> : draft.lyrics.map(renderBlockItem)}
          </div>
        </div>

        {/* EDITOR DER */}
        <div style={styles.card}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <p>
                    {editingBlockId ? (
                        <span><i className='bi bi-pencil'></i> Editar bloque</span>
                    ) : (
                        <span><i className='bi bi-plus-lg'></i> Nuevo bloque</span>
                    )}
                </p>
                {editingBlockId && <button className='Btn__Adsl--RedOutlineTransparent' onClick={resetBlockForm}><i className='bi bi-x-lg'></i> Cancelar</button>}
            </div>

            <div style={{ marginBottom: 12 }}>
                <label style={styles.label}>Tipo</label>
                <div style={{ display: "flex", gap: 15 }}>
                <label style={{ cursor: "pointer" }}><input type="radio" checked={blockForm.type === "lyric"} onChange={() => setBlockForm(p => ({ ...p, type: "lyric" }))} /> Letra</label>
                <label style={{ cursor: "pointer" }}><input type="radio" checked={blockForm.type === "instrumental"} onChange={() => setBlockForm(p => ({ ...p, type: "instrumental" }))} /> Instrumental</label>
                </div>
            </div>

            {/* FORMULARIO */}
            {blockForm.type === "instrumental" ? (
                <div style={{ marginBottom: 12 }}>
                <label style={styles.label}>Mostrar Ícono</label>
                <select style={styles.input} value={blockForm.display} onChange={(e) => setBlockForm(p => ({ ...p, display: e.target.value }))}>
                    <option value="icon">Sí (♪)</option>
                    <option value="none">No (Vacío)</option>
                </select>
                </div>
            ) : (
                <>
                <div style={{ marginBottom: 12 }}>
                    <label style={styles.label}>Modo</label>
                    <select style={styles.input} value={blockForm.mode} onChange={(e) => setBlockForm(p => ({ ...p, mode: e.target.value }))}>
                    <option value="uniform">Uniforme (Línea completa)</option>
                    <option value="parts">Por Partes (Silábico)</option>
                    </select>
                </div>

                {blockForm.mode === "uniform" ? (
                    <div style={{ marginBottom: 12 }}>
                    <label style={styles.label}>Texto</label>
                    <input style={styles.input} placeholder="Ej. Tal vez me olvide..." value={blockForm.text} onChange={(e) => setBlockForm(p => ({ ...p, text: e.target.value }))} autoFocus />
                    </div>
                ) : (
                    <div style={{ marginBottom: 12, background: "#fdfdfd", padding: 8, border: "1px dashed #ccc", borderRadius: 4 }}>
                    <label style={styles.label}>Partes (Texto + Duración)</label>
                    {blockForm.parts.map((part, idx) => (
                        <div key={idx} style={{ display: "flex", gap: 5, marginBottom: 5, alignItems: "center" }}>
                        <input style={{ ...styles.input, flex: 2 }} placeholder="Sílaba" value={part.text} onChange={(e) => { const n = [...blockForm.parts]; n[idx].text = e.target.value; setBlockForm(p => ({ ...p, parts: n })); }} />
                        <input style={{ ...styles.input, width: 60 }} type="number" value={part.durationMs} onChange={(e) => { const n = [...blockForm.parts]; n[idx].durationMs = Number(e.target.value); setBlockForm(p => ({ ...p, parts: n })); }} />
                        <button
                            onMouseDown={() => startMeasuringPart(idx)} onMouseUp={() => stopMeasuringPart(idx)}
                            onTouchStart={() => startMeasuringPart(idx)} onTouchEnd={() => stopMeasuringPart(idx)}
                            style={{ ...styles.btn, padding: "0 8px", height: "36px", background: measuringPartIndex === idx ? "#ffeb3b" : "#eee", border: measuringPartIndex === idx ? "1px solid #d4b600" : "1px solid #ccc" }}
                            title="Mantén para medir">
                            {measuringPartIndex === idx ? "⏱" : "●"}
                        </button>
                        <button onClick={() => setBlockForm(p => ({ ...p, parts: p.parts.filter((_, i) => i !== idx) }))} style={{ ...styles.btn, ...styles.btnDanger, padding: "0 8px", height: "36px" }}>x</button>
                        </div>
                    ))}
                    <button onClick={() => setBlockForm(p => ({ ...p, parts: [...p.parts, { text: "", durationMs: 1000 }] }))} style={{ fontSize: 12, marginTop: 5, cursor: "pointer", background: "none", border: "none", color: "#007bff" }}>+ Agregar Parte</button>
                    </div>
                )}
                </>
            )}

            {/* DURACIÓN */}
            {(blockForm.type === "instrumental" || blockForm.mode === "uniform") && (
                <div style={{ marginBottom: 12 }}>
                <label style={styles.label}>Duración Total (ms)</label>
                <div style={{ display: "flex", gap: 8 }}>
                    <input type="number" style={styles.input} value={blockForm.durationMs} onChange={(e) => setBlockForm(p => ({ ...p, durationMs: Number(e.target.value) }))} />
                    <button
                    onMouseDown={toggleTiming} onMouseUp={toggleTiming}
                    onTouchStart={toggleTiming} onTouchEnd={toggleTiming}
                    style={{ ...styles.btn, ...styles.tapBtn, background: isTiming ? "#ffeb3b" : "#eee", color: isTiming ? "#000" : "#333" }}>
                    {isTiming ? "■ SOLTAR" : "● MEDIR"}
                    </button>
                </div>
                </div>
            )}

            <button className='Btn__Adsl--Red' onClick={saveBlock}>
                {editingBlockId ? <span><i className='bi bi-floppy'></i> Guardar cambio de bloque</span> : <span><i className='bi bi-plus-circle'></i> Agregar bloque</span>}
            </button>
        </div>
      </div>

      <button className='Btn__Adsl--Red mx-auto w-100 mt-5' onClick={handleSaveSong}><i className='bi bi-floppy'></i> Guardar canción</button>

      {/* MODAL DE CONFIRMACIÓN */}
      {showConfirm && (
          <Confirm 
             message={confirmConfig?.message}
             textYesButton={confirmConfig?.textYesButton}
             textNoButton={confirmConfig?.textNoButton}
             actionToYesButton={confirmConfig?.actionToYesButton}
             setShowModal={setShowConfirm}
             setConfirmData={setConfirmConfig}
          />
      )}

      {/* NUEVO: MODAL IMPORTAR TEXTO (OVERLAY) */}
      {showImportTextModal && (
          <div style={styles.modalOverlay}>
              <div style={styles.modalContent}>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                      <h4 style={{margin:0}}>Importar Letra (Texto Plano)</h4>
                      <button onClick={() => setShowImportTextModal(false)} style={{background:'none', border:'none', fontSize:20, cursor:'pointer'}}>×</button>
                  </div>
                  
                  <div style={{fontSize: '0.85rem', color:'#666', background:'#f0f8ff', padding:10, borderRadius:4}}>
                      <p style={{margin:0, marginBottom:5}}><strong>Instrucciones:</strong></p>
                      <ul style={{margin:0, paddingLeft:20}}>
                          <li>Pega tu letra línea por línea.</li>
                          <li>Usa <strong>Enter</strong> para separar bloques uniformes.</li>
                          <li>Escribe <strong>#</strong> en una línea para crear un bloque Instrumental.</li>
                          <li>Usa <strong>*</strong> para separar sílabas (Ej: <em>sa* lu* dos</em>).</li>
                      </ul>
                  </div>

                  <textarea 
                      style={styles.textarea} 
                      placeholder="Pega aquí tu letra..." 
                      value={textToImport}
                      onChange={(e) => setTextToImport(e.target.value)}
                  />

                  <div style={{display:'flex', justifyContent:'flex-end', gap:10}}>
                      <button onClick={() => setShowImportTextModal(false)} style={{...styles.btn}}>Cancelar</button>
                      <button onClick={processImportedText} style={{...styles.btn, ...styles.btnPrimary}}>Procesar e Importar</button>
                  </div>
              </div>
          </div>
      )}
    </div>
  )
}

export { KaraokeSongCreator };









// Funcional
// import React, { useState, useEffect, useCallback, useMemo } from 'react'
// import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage'

// import { Confirm } from './../../../Commons/Modals/Confirm'


// /**
//  * ==============================================================================
//  * ESTILOS INLINE
//  * ==============================================================================
//  */
// const styles = {
//   container: { display: "flex", flexDirection: "column", gap: 20 },
//   row: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
//   card: { background: "#fff", border: "1px solid #ddd", borderRadius: 8, padding: 16, boxShadow: "0 2px 5px rgba(0,0,0,0.05)" },
//   label: { fontWeight: 700, fontSize: "0.85rem", color: "#555", display: "block", marginBottom: 4 },
//   input: { padding: 8, borderRadius: 4, border: "1px solid #ccc", width: "100%" },
//   textarea: { padding: 10, borderRadius: 4, border: "1px solid #ccc", width: "100%", minHeight: "200px", fontFamily: "monospace", fontSize: "0.9rem" },
//   btn: { padding: "8px 16px", borderRadius: 4, border: "none", background: "#eee", cursor: "pointer", fontWeight: 600 },
//   btnPrimary: { background: "#007bff", color: "#fff" },
//   btnDanger: { background: "#dc3545", color: "#fff" },
//   blockList: { display: "flex", flexDirection: "column", gap: 8, maxHeight: "500px", overflowY: "auto", border: "1px solid #eee", padding: 8, borderRadius: 4 },
//   blockItem: { display: "flex", alignItems: "center", gap: 10, background: "#f9f9f9", border: "1px solid #eee", padding: 8, borderRadius: 4, cursor: "pointer" },
//   activeBlock: { borderLeft: "4px solid #007bff", background: "#f0f8ff" },
//   tapBtn: { minWidth: "80px", border: "1px solid #bbb", fontWeight: "bold" },
//   statusBadge: { fontSize: '0.75rem', padding: '2px 6px', borderRadius: '4px', marginLeft: '10px', fontWeight: 'bold' },
//   // Estilos para el nuevo modal interno (Overlay simple)
//   modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1100 },
//   modalContent: { background: "#fff", padding: 20, borderRadius: 8, width: "600px", maxWidth: "90%", display: "flex", flexDirection: "column", gap: 15, boxShadow: "0 4px 15px rgba(0,0,0,0.3)" }
// }

// /**
//  * ==============================================================================
//  * COMPONENTE: KaraokeSongCreator
//  * ==============================================================================
//  */
// const KaraokeSongCreator = ({ songs, selectedSongId, onSongsChanged, onRequestClose, generateUuid }) => {
  
//   // -- ESTADOS GLOBALES --
//   const [draft, setDraft] = useState({ id: "", name: "", artist: "", url: "", lyrics: [] })
//   const [originalState, setOriginalState] = useState(null)
//   const [isNewSong, setIsNewSong] = useState(false)
//   const [editingBlockId, setEditingBlockId] = useState(null)

//   // -- ESTADOS MODAL CONFIRMACIÓN --
//   const [showConfirm, setShowConfirm] = useState(false)
//   const [confirmConfig, setConfirmConfig] = useState(null)

//   // -- ESTADOS MODAL IMPORTAR TEXTO (NUEVO) --
//   const [showImportTextModal, setShowImportTextModal] = useState(false)
//   const [textToImport, setTextToImport] = useState("")

//   // -- ESTADO FORMULARIO BLOQUE --
//   const [blockForm, setBlockForm] = useState({
//     type: "lyric", mode: "uniform", text: "", durationMs: 3000, display: "icon", parts: [],
//   })

//   // -- ESTADO HERRAMIENTAS TAP --
//   const [isTiming, setIsTiming] = useState(false)
//   const [timingStart, setTimingStart] = useState(0)
//   const [measuringPartIndex, setMeasuringPartIndex] = useState(null)

//   const { showMessage } = useFloatMessage()

//   /** CARGA INICIAL */
//   useEffect(() => {
//     const current = songs.find((s) => s.id === selectedSongId)
//     if (current) {
//       const data = JSON.parse(JSON.stringify(current))
//       setDraft(data)
//       setOriginalState(JSON.stringify(data))
//       setIsNewSong(false)
//     } else {
//       createNewDraft()
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [selectedSongId, songs])

//   /** DETECCIÓN DE CAMBIOS */
//   const hasUnsavedChanges = useMemo(() => {
//       return JSON.stringify(draft) !== originalState
//   }, [draft, originalState])

//   // -- GESTIÓN DEL DRAFT --
//   const createNewDraft = () => {
//     const newS = { id: generateUuid(), name: "Nueva Canción", artist: "Artista Desconocido", url: "", lyrics: [], createdAt: new Date().toISOString() }
//     setDraft(newS)
//     setOriginalState(JSON.stringify(newS))
//     setIsNewSong(true)
//     setEditingBlockId(null)
//   }

//   const updateDraftField = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }))

//   // -- ACCIONES PRINCIPALES --

//   const requestClean = () => {
//       setConfirmConfig({
//           message: "¿Estás seguro de limpiar el editor? Se perderán los cambios no guardados.",
//           textYesButton: "Sí, limpiar",
//           textNoButton: "Cancelar",
//           actionToYesButton: async () => {
//               createNewDraft()
//               return { success: true }
//           }
//       })
//       setShowConfirm(true)
//   }

//   const requestDuplicate = () => {
//       setConfirmConfig({
//           message: `¿Duplicar la canción "${draft.name}"?`,
//           textYesButton: "Duplicar",
//           textNoButton: "Cancelar",
//           actionToYesButton: async () => {
//               const copy = { ...draft, id: generateUuid(), name: `${draft.name} (Copia)`, createdAt: new Date().toISOString() }
//               onSongsChanged(copy, copy.id)
//               return { success: true }
//           }
//       })
//       setShowConfirm(true)
//   }

//   const handleSaveSong = async () => {
//     if (!draft.name.trim()) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'La canción necesita un nombre' })
    
//     const now = new Date().toISOString()
//     const songToSave = { ...draft, updatedAt: now }

//     setOriginalState(JSON.stringify(songToSave))
//     onSongsChanged(songToSave, songToSave.id)
//   }

//   const handleDeleteSong = () => {
//     if (!window.confirm("¿Borrar canción? Esta acción no se puede deshacer.")) return
//     const nextSongs = songs.filter((s) => s.id !== draft.id)
//     onSongsChanged(nextSongs, null)
//   }

//   // -- IMPORT / EXPORT JSON --
//   const copyJson = () => {
//       navigator.clipboard.writeText(JSON.stringify(draft, null, 2))
//         .then(() => showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'JSON copiado al portapapeles' }))
//   }

//   const importJson = async () => {
//     try {
//       const text = await navigator.clipboard.readText()
//       const obj = JSON.parse(text)
//       if (!Array.isArray(obj.lyrics)) throw new Error()
      
//       const imported = { ...obj, id: generateUuid(), name: obj.name + " (Importada)" }
//       setDraft(imported)
//       showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Canción importada (Recuerda guardar)' })
//     } catch {
//       showMessage({ type: MESSAGE_TYPE.BAD, text: 'JSON inválido' })
//     }
//   }

//   /**
//    * NUEVO: LÓGICA DE IMPORTACIÓN DE TEXTO PLANO
//    * Convierte texto crudo en bloques estructurados.
//    */
//   const processImportedText = () => {
//       if (!textToImport.trim()) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Escribe algo de texto para importar' })

//       const lines = textToImport.split(/\r?\n/) // Soporte para saltos de línea Windows/Unix
//       const newBlocks = []

//       lines.forEach(rawLine => {
//           const line = rawLine.trim()
          
//           // Caso 0: Línea vacía -> Ignorar (como solicitaste)
//           if (!line) return 

//           // Caso 1: Instrumental (#)
//           if (line.startsWith('#')) {
//               newBlocks.push({
//                   id: generateUuid(),
//                   type: 'instrumental',
//                   display: 'icon',
//                   mode: 'uniform',
//                   durationMs: 3000, // Tiempo default
//                   parts: []
//               })
//               return
//           }

//           // Caso 2: Bloque por Partes/Sílabas (*)
//           if (line.includes('*')) {
//               // Separamos por * y filtramos vacíos
//               const partsRaw = line.split('*').map(p => p.trim()).filter(p => p !== '')
              
//               if (partsRaw.length > 0) {
//                   const partsObj = partsRaw.map(textPart => ({
//                       text: textPart,
//                       durationMs: 1000 // Tiempo default por parte
//                   }))
                  
//                   newBlocks.push({
//                       id: generateUuid(),
//                       type: 'lyric',
//                       mode: 'parts',
//                       text: '', // No se usa en modo parts
//                       durationMs: partsObj.length * 1000,
//                       parts: partsObj
//                   })
//               }
//               return
//           }

//           // Caso 3: Bloque Uniforme (Texto normal + Enter)
//           newBlocks.push({
//               id: generateUuid(),
//               type: 'lyric',
//               mode: 'uniform',
//               text: line,
//               durationMs: 3000, // Tiempo default
//               parts: []
//           })
//       })

//       // Agregamos los bloques al final del borrador actual
//       setDraft(prev => ({
//           ...prev,
//           lyrics: [...prev.lyrics, ...newBlocks]
//       }))

//       showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `${newBlocks.length} bloques generados correctamente` })
//       setTextToImport("")
//       setShowImportTextModal(false)
//   }

//   // -- GESTIÓN DE BLOQUES (CRUD INTERNO) --
//   const resetBlockForm = () => {
//     setBlockForm({ type: "lyric", mode: "uniform", text: "", durationMs: 3000, display: "icon", parts: [] })
//     setEditingBlockId(null)
//   }

//   const loadBlockToEdit = (block) => {
//     setBlockForm({
//       type: block.type || "lyric",
//       mode: block.mode || "uniform",
//       text: block.text || "",
//       durationMs: block.durationMs || 2000,
//       display: block.display || "icon",
//       parts: block.parts ? JSON.parse(JSON.stringify(block.parts)) : [],
//     })
//     setEditingBlockId(block.id)
//   }

//   const saveBlock = () => {
//     const newBlock = { id: editingBlockId || generateUuid(), type: blockForm.type, durationMs: Number(blockForm.durationMs) }

//     if (blockForm.type === "lyric") {
//       newBlock.mode = blockForm.mode
//       if (blockForm.mode === "uniform") {
//         newBlock.text = blockForm.text
//       } else {
//         if (blockForm.parts.length === 0) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Debes agregar al menos una parte' })
//         const totalPartsMs = blockForm.parts.reduce((acc, p) => acc + (Number(p.durationMs) || 0), 0)
//         newBlock.parts = blockForm.parts
//         newBlock.durationMs = totalPartsMs
//       }
//     } else {
//       newBlock.display = blockForm.display
//     }

//     let newLyrics = [...draft.lyrics]
//     if (editingBlockId) {
//       const idx = newLyrics.findIndex((b) => b.id === editingBlockId)
//       if (idx >= 0) newLyrics[idx] = newBlock
//     } else {
//       newLyrics.push(newBlock)
//     }

//     setDraft((prev) => ({ ...prev, lyrics: newLyrics }))
    
//     showMessage({ 
//         type: MESSAGE_TYPE.SUCCESS, 
//         text: editingBlockId ? 'Bloque actualizado correctamente' : 'Bloque agregado correctamente' 
//     })

//     resetBlockForm()
//   }

//   const removeBlock = (blockId) => {
//     setDraft((prev) => ({ ...prev, lyrics: prev.lyrics.filter((b) => b.id !== blockId) }))
//     if (editingBlockId === blockId) resetBlockForm()
//   }

//   const moveBlock = (index, direction) => {
//     const newLyrics = [...draft.lyrics]
//     if (direction === -1 && index > 0) [newLyrics[index], newLyrics[index - 1]] = [newLyrics[index - 1], newLyrics[index]]
//     else if (direction === 1 && index < newLyrics.length - 1) [newLyrics[index], newLyrics[index + 1]] = [newLyrics[index + 1], newLyrics[index]]
//     setDraft((prev) => ({ ...prev, lyrics: newLyrics }))
//   }

//   // -- HERRAMIENTAS TAP --
//   const toggleTiming = () => {
//     if (!isTiming) {
//       setIsTiming(true)
//       setTimingStart(performance.now())
//     } else {
//       const diff = Math.round(performance.now() - timingStart)
//       setIsTiming(false)
//       setBlockForm(prev => ({ ...prev, durationMs: diff }))
//     }
//   }

//   const startMeasuringPart = (index) => {
//     setMeasuringPartIndex(index)
//     setTimingStart(performance.now())
//   }

//   const stopMeasuringPart = (index) => {
//     if (measuringPartIndex !== index) return
//     const diff = Math.round(performance.now() - timingStart)
//     const newParts = [...blockForm.parts]
//     newParts[index].durationMs = diff
//     setBlockForm(prev => ({ ...prev, parts: newParts }))
//     setMeasuringPartIndex(null)
//   }

//   // -- RENDER HELPERS --
//   const renderBlockItem = (block, index) => {
//     let title = block.type === "instrumental" ? `🎵 INSTRUMENTAL (${block.display})` : (block.mode === "uniform" ? block.text : `[PARTS] ${block.parts?.map(p => p.text).join("")}`)
//     const isEditing = editingBlockId === block.id

//     return (
//       <div key={block.id} style={{ ...styles.blockItem, ...(isEditing ? styles.activeBlock : {}) }}>
//         <div style={{ fontWeight: "bold", color: "#888", width: 20 }}>{index + 1}.</div>
//         <div style={{ flex: 1, overflow: "hidden" }} onClick={() => loadBlockToEdit(block)}>
//           <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title || "(Vacío)"}</div>
//           <div style={{ fontSize: 11, color: "#666" }}>{block.durationMs}ms</div>
//         </div>
//         <div style={{ display: "flex", flexDirection: "column" }}>
//           <button onClick={() => moveBlock(index, -1)} style={{ fontSize: 10, cursor: "pointer", border: "none", background: "none" }}>▲</button>
//           <button onClick={() => moveBlock(index, 1)} style={{ fontSize: 10, cursor: "pointer", border: "none", background: "none" }}>▼</button>
//         </div>
//         <button onClick={(e) => { e.stopPropagation(); removeBlock(block.id); }} style={{ ...styles.btn, ...styles.btnDanger, padding: "2px 6px", fontSize: 10 }}>X</button>
//       </div>
//     )
//   }

//   return (
//     <div style={styles.container}>
//         {/* CABECERA */}
//         <div className='LyricsGenerator__Editor__MetadataSong Element--Shadow'>
//             <div className='LyricsGenerator__Editor__MetadataSong__Header'>
//                 <div style={{display:'flex', alignItems:'center', flexWrap:'wrap', gap: 10}}>
//                     {isNewSong ? (
//                         <span className='LyricsGenerator__Text--Title'><i className='bi bi-plus-circle'></i> Nueva canción</span>
//                     ) : (
//                         <span className='LyricsGenerator__Text--Title'><i className='bi bi-pencil'></i> Editando canción</span>
//                     )}

//                     {/* INDICADORES DE ESTADO */}
//                     {hasUnsavedChanges && (
//                         <span style={{...styles.statusBadge, background:'#fff3cd', color:'#856404', border:'1px solid #ffeeba'}}>
//                             <i className="bi bi-exclamation-triangle-fill"></i> Cambios sin guardar
//                         </span>
//                     )}
//                     {!hasUnsavedChanges && draft.updatedAt && (
//                         <span style={{...styles.statusBadge, color:'#888', fontWeight:'normal'}}>
//                             Guardado: {new Date(draft.updatedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
//                         </span>
//                     )}
//                 </div>

//                 <div className='LyricsGenerator__Editor__MetadataSong__Buttons'>
//                     <button className='Btn__Adsl--Red LyricsGenerator__Editor__MetadataSong__Buttons__Button' onClick={handleSaveSong}><i className='bi bi-floppy'></i> Guardar</button>
//                     {/* Botones con modal de confirmación */}
//                     <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Editor__MetadataSong__Buttons__Button' onClick={requestDuplicate}><i className='bi bi-files'></i> Duplicar</button>
//                     <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Editor__MetadataSong__Buttons__Button' onClick={requestClean}><i className='bi bi-eraser'></i> Limpiar</button>
//                 </div>
//             </div>

//             <div style={styles.row}>
//                 <div style={{ flex: 1 }}><label style={styles.label}>Nombre</label><input style={styles.input} value={draft.name} onChange={(e) => updateDraftField("name", e.target.value)} /></div>
//                 <div style={{ flex: 1 }}><label style={styles.label}>Artista</label><input style={styles.input} value={draft.artist} onChange={(e) => updateDraftField("artist", e.target.value)} /></div>
//                 <div style={{ flex: 1 }}><label style={styles.label}>URL</label><input style={styles.input} value={draft.url} onChange={(e) => updateDraftField("url", e.target.value)} /></div>
//             </div>

//             {/* BARRA DE UTILIDADES */}
//             <div style={{ marginTop: 10, display: "flex", gap: 15, alignItems: "center" }}>
//                 <button onClick={() => setShowImportTextModal(true)} style={{ fontSize: 12, background: "none", border: "none", color: "#007bff", cursor: "pointer", textDecoration: "underline", fontWeight: "bold" }}><i className='bi bi-file-earmark-text'></i> Importar Letra (Texto)</button>
//                 <div style={{width: 1, height: 15, background: "#ccc"}}></div>
//                 <button onClick={copyJson} style={{ fontSize: 12, background: "none", border: "none", color: "#666", cursor: "pointer", textDecoration: "underline" }}><i className='bi bi-copy'></i> Copiar JSON</button>
//                 <button onClick={importJson} style={{ fontSize: 12, background: "none", border: "none", color: "#666", cursor: "pointer", textDecoration: "underline" }}><i className='bi bi-clipboard2'></i> Pegar JSON</button>
                
//                 {!isNewSong && (
//                     <button onClick={handleDeleteSong} style={{ fontSize: 12, background: "none", border: "none", color: "red", cursor: "pointer", marginLeft: "auto", textDecoration: "underline" }}><i className='bi bi-trash'></i> Eliminar</button>
//                 )}
//             </div>
//       </div>

//       {/* WORKSPACE */}
//       <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
//         {/* LISTA IZQ */}
//         <div>
//           <label style={{ ...styles.label, marginBottom: 8 }}>Línea de Tiempo ({draft.lyrics.length} bloques)</label>
//           <div style={styles.blockList}>
//             {draft.lyrics.length === 0 ? <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Sin bloques</div> : draft.lyrics.map(renderBlockItem)}
//           </div>
//         </div>

//         {/* EDITOR DER */}
//         <div style={styles.card}>
//             <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
//                 <p>
//                     {editingBlockId ? (
//                         <span><i className='bi bi-pencil'></i> Editar bloque</span>
//                     ) : (
//                         <span><i className='bi bi-plus-lg'></i> Nuevo bloque</span>
//                     )}
//                 </p>
//                 {editingBlockId && <button className='Btn__Adsl--RedOutlineTransparent' onClick={resetBlockForm}><i className='bi bi-x-lg'></i> Cancelar</button>}
//             </div>

//             <div style={{ marginBottom: 12 }}>
//                 <label style={styles.label}>Tipo</label>
//                 <div style={{ display: "flex", gap: 15 }}>
//                 <label style={{ cursor: "pointer" }}><input type="radio" checked={blockForm.type === "lyric"} onChange={() => setBlockForm(p => ({ ...p, type: "lyric" }))} /> Letra</label>
//                 <label style={{ cursor: "pointer" }}><input type="radio" checked={blockForm.type === "instrumental"} onChange={() => setBlockForm(p => ({ ...p, type: "instrumental" }))} /> Instrumental</label>
//                 </div>
//             </div>

//             {/* FORMULARIO */}
//             {blockForm.type === "instrumental" ? (
//                 <div style={{ marginBottom: 12 }}>
//                 <label style={styles.label}>Mostrar Ícono</label>
//                 <select style={styles.input} value={blockForm.display} onChange={(e) => setBlockForm(p => ({ ...p, display: e.target.value }))}>
//                     <option value="icon">Sí (♪)</option>
//                     <option value="none">No (Vacío)</option>
//                 </select>
//                 </div>
//             ) : (
//                 <>
//                 <div style={{ marginBottom: 12 }}>
//                     <label style={styles.label}>Modo</label>
//                     <select style={styles.input} value={blockForm.mode} onChange={(e) => setBlockForm(p => ({ ...p, mode: e.target.value }))}>
//                     <option value="uniform">Uniforme (Línea completa)</option>
//                     <option value="parts">Por Partes (Silábico)</option>
//                     </select>
//                 </div>

//                 {blockForm.mode === "uniform" ? (
//                     <div style={{ marginBottom: 12 }}>
//                     <label style={styles.label}>Texto</label>
//                     <input style={styles.input} placeholder="Ej. Tal vez me olvide..." value={blockForm.text} onChange={(e) => setBlockForm(p => ({ ...p, text: e.target.value }))} autoFocus />
//                     </div>
//                 ) : (
//                     <div style={{ marginBottom: 12, background: "#fdfdfd", padding: 8, border: "1px dashed #ccc", borderRadius: 4 }}>
//                     <label style={styles.label}>Partes (Texto + Duración)</label>
//                     {blockForm.parts.map((part, idx) => (
//                         <div key={idx} style={{ display: "flex", gap: 5, marginBottom: 5, alignItems: "center" }}>
//                         <input style={{ ...styles.input, flex: 2 }} placeholder="Sílaba" value={part.text} onChange={(e) => { const n = [...blockForm.parts]; n[idx].text = e.target.value; setBlockForm(p => ({ ...p, parts: n })); }} />
//                         <input style={{ ...styles.input, width: 60 }} type="number" value={part.durationMs} onChange={(e) => { const n = [...blockForm.parts]; n[idx].durationMs = Number(e.target.value); setBlockForm(p => ({ ...p, parts: n })); }} />
//                         <button
//                             onMouseDown={() => startMeasuringPart(idx)} onMouseUp={() => stopMeasuringPart(idx)}
//                             onTouchStart={() => startMeasuringPart(idx)} onTouchEnd={() => stopMeasuringPart(idx)}
//                             style={{ ...styles.btn, padding: "0 8px", height: "36px", background: measuringPartIndex === idx ? "#ffeb3b" : "#eee", border: measuringPartIndex === idx ? "1px solid #d4b600" : "1px solid #ccc" }}
//                             title="Mantén para medir">
//                             {measuringPartIndex === idx ? "⏱" : "●"}
//                         </button>
//                         <button onClick={() => setBlockForm(p => ({ ...p, parts: p.parts.filter((_, i) => i !== idx) }))} style={{ ...styles.btn, ...styles.btnDanger, padding: "0 8px", height: "36px" }}>x</button>
//                         </div>
//                     ))}
//                     <button onClick={() => setBlockForm(p => ({ ...p, parts: [...p.parts, { text: "", durationMs: 1000 }] }))} style={{ fontSize: 12, marginTop: 5, cursor: "pointer", background: "none", border: "none", color: "#007bff" }}>+ Agregar Parte</button>
//                     </div>
//                 )}
//                 </>
//             )}

//             {/* DURACIÓN */}
//             {(blockForm.type === "instrumental" || blockForm.mode === "uniform") && (
//                 <div style={{ marginBottom: 12 }}>
//                 <label style={styles.label}>Duración Total (ms)</label>
//                 <div style={{ display: "flex", gap: 8 }}>
//                     <input type="number" style={styles.input} value={blockForm.durationMs} onChange={(e) => setBlockForm(p => ({ ...p, durationMs: Number(e.target.value) }))} />
//                     <button
//                     onMouseDown={toggleTiming} onMouseUp={toggleTiming}
//                     onTouchStart={toggleTiming} onTouchEnd={toggleTiming}
//                     style={{ ...styles.btn, ...styles.tapBtn, background: isTiming ? "#ffeb3b" : "#eee", color: isTiming ? "#000" : "#333" }}>
//                     {isTiming ? "■ SOLTAR" : "● MEDIR"}
//                     </button>
//                 </div>
//                 </div>
//             )}

//             <button className='Btn__Adsl--Red' onClick={saveBlock}>
//                 {editingBlockId ? <span><i className='bi bi-floppy'></i> Guardar cambio de bloque</span> : <span><i className='bi bi-plus-circle'></i> Agregar bloque</span>}
//             </button>
//         </div>
//       </div>

//       <button className='Btn__Adsl--Red mx-auto w-100 mt-5' onClick={handleSaveSong}><i className='bi bi-floppy'></i> Guardar canción</button>

//       {/* MODAL DE CONFIRMACIÓN */}
//       {showConfirm && (
//           <Confirm 
//              message={confirmConfig?.message}
//              textYesButton={confirmConfig?.textYesButton}
//              textNoButton={confirmConfig?.textNoButton}
//              actionToYesButton={confirmConfig?.actionToYesButton}
//              setShowModal={setShowConfirm}
//              setConfirmData={setConfirmConfig}
//           />
//       )}

//       {/* NUEVO: MODAL IMPORTAR TEXTO (OVERLAY) */}
//       {showImportTextModal && (
//           <div style={styles.modalOverlay}>
//               <div style={styles.modalContent}>
//                   <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
//                       <h4 style={{margin:0}}>Importar Letra (Texto Plano)</h4>
//                       <button onClick={() => setShowImportTextModal(false)} style={{background:'none', border:'none', fontSize:20, cursor:'pointer'}}>×</button>
//                   </div>
                  
//                   <div style={{fontSize: '0.85rem', color:'#666', background:'#f0f8ff', padding:10, borderRadius:4}}>
//                       <p style={{margin:0, marginBottom:5}}><strong>Instrucciones:</strong></p>
//                       <ul style={{margin:0, paddingLeft:20}}>
//                           <li>Pega tu letra línea por línea.</li>
//                           <li>Usa <strong>Enter</strong> para separar bloques uniformes.</li>
//                           <li>Escribe <strong>#</strong> en una línea para crear un bloque Instrumental.</li>
//                           <li>Usa <strong>*</strong> para separar sílabas (Ej: <em>sa* lu* dos</em>).</li>
//                       </ul>
//                   </div>

//                   <textarea 
//                       style={styles.textarea} 
//                       placeholder="Pega aquí tu letra..." 
//                       value={textToImport}
//                       onChange={(e) => setTextToImport(e.target.value)}
//                   />

//                   <div style={{display:'flex', justifyContent:'flex-end', gap:10}}>
//                       <button onClick={() => setShowImportTextModal(false)} style={{...styles.btn}}>Cancelar</button>
//                       <button onClick={processImportedText} style={{...styles.btn, ...styles.btnPrimary}}>Procesar e Importar</button>
//                   </div>
//               </div>
//           </div>
//       )}
//     </div>
//   )
// }

// export { KaraokeSongCreator };






































// Funcional (en servidor)
// /**
//  * ==============================================================================
//  * ESTILOS INLINE
//  * ==============================================================================
//  */
// const styles = {
//   container: { display: "flex", flexDirection: "column", gap: 20 },
//   row: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
//   card: { background: "#fff", border: "1px solid #ddd", borderRadius: 8, padding: 16, boxShadow: "0 2px 5px rgba(0,0,0,0.05)" },
//   label: { fontWeight: 700, fontSize: "0.85rem", color: "#555", display: "block", marginBottom: 4 },
//   input: { padding: 8, borderRadius: 4, border: "1px solid #ccc", width: "100%" },
//   btn: { padding: "8px 16px", borderRadius: 4, border: "none", background: "#eee", cursor: "pointer", fontWeight: 600 },
//   btnPrimary: { background: "#007bff", color: "#fff" },
//   btnDanger: { background: "#dc3545", color: "#fff" },
//   blockList: { display: "flex", flexDirection: "column", gap: 8, maxHeight: "500px", overflowY: "auto", border: "1px solid #eee", padding: 8, borderRadius: 4 },
//   blockItem: { display: "flex", alignItems: "center", gap: 10, background: "#f9f9f9", border: "1px solid #eee", padding: 8, borderRadius: 4, cursor: "pointer" },
//   activeBlock: { borderLeft: "4px solid #007bff", background: "#f0f8ff" },
//   tapBtn: { minWidth: "80px", border: "1px solid #bbb", fontWeight: "bold" },
//   statusBadge: { fontSize: '0.75rem', padding: '2px 6px', borderRadius: '4px', marginLeft: '10px', fontWeight: 'bold' }
// }

// /**
//  * ==============================================================================
//  * COMPONENTE: KaraokeSongCreator
//  * ==============================================================================
//  */
// const KaraokeSongCreator = ({ songs, selectedSongId, onSongsChanged, onRequestClose, generateUuid }) => {
  
//   // -- ESTADOS GLOBALES --
//   const [draft, setDraft] = useState({ id: "", name: "", artist: "", url: "", lyrics: [] })
//   const [originalState, setOriginalState] = useState(null)
//   const [isNewSong, setIsNewSong] = useState(false)
//   const [editingBlockId, setEditingBlockId] = useState(null)

//   // -- ESTADOS MODAL CONFIRMACIÓN --
//   const [showConfirm, setShowConfirm] = useState(false)
//   const [confirmConfig, setConfirmConfig] = useState(null)

//   // -- ESTADO FORMULARIO BLOQUE --
//   const [blockForm, setBlockForm] = useState({
//     type: "lyric", mode: "uniform", text: "", durationMs: 3000, display: "icon", parts: [],
//   })

//   // -- ESTADO HERRAMIENTAS TAP --
//   const [isTiming, setIsTiming] = useState(false)
//   const [timingStart, setTimingStart] = useState(0)
//   const [measuringPartIndex, setMeasuringPartIndex] = useState(null)

//   const { showMessage } = useFloatMessage()

//   /** CARGA INICIAL */
//   useEffect(() => {
//     const current = songs.find((s) => s.id === selectedSongId)
//     if (current) {
//       const data = JSON.parse(JSON.stringify(current))
//       setDraft(data)
//       setOriginalState(JSON.stringify(data))
//       setIsNewSong(false)
//     } else {
//       createNewDraft()
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [selectedSongId, songs])

//   /** DETECCIÓN DE CAMBIOS */
//   const hasUnsavedChanges = useMemo(() => {
//       return JSON.stringify(draft) !== originalState
//   }, [draft, originalState])

//   // -- GESTIÓN DEL DRAFT --
//   const createNewDraft = () => {
//     const newS = { id: generateUuid(), name: "Nueva Canción", artist: "Artista Desconocido", url: "", lyrics: [], createdAt: new Date().toISOString() }
//     setDraft(newS)
//     setOriginalState(JSON.stringify(newS))
//     setIsNewSong(true)
//     setEditingBlockId(null)
//   }

//   const updateDraftField = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }))

//   // -- ACCIONES PRINCIPALES (GUARDAR, LIMPIAR, DUPLICAR) --

//   const requestClean = () => {
//       setConfirmConfig({
//           message: "¿Estás seguro de limpiar el editor? Se perderán los cambios no guardados.",
//           textYesButton: "Sí, limpiar",
//           textNoButton: "Cancelar",
//           actionToYesButton: async () => {
//               createNewDraft()
//               return { success: true }
//           }
//       })
//       setShowConfirm(true)
//   }

//   const requestDuplicate = () => {
//       setConfirmConfig({
//           message: `¿Duplicar la canción "${draft.name}"?`,
//           textYesButton: "Duplicar",
//           textNoButton: "Cancelar",
//           actionToYesButton: async () => {
//               const copy = { ...draft, id: generateUuid(), name: `${draft.name} (Copia)`, createdAt: new Date().toISOString() }
//               // Enviamos al padre para que guarde la copia inmediatamente
//               onSongsChanged(copy, copy.id)
//               return { success: true }
//           }
//       })
//       setShowConfirm(true)
//   }

//   const handleSaveSong = async () => {
//     if (!draft.name.trim()) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'La canción necesita un nombre' })
    
//     const now = new Date().toISOString()
//     const songToSave = { ...draft, updatedAt: now }

//     setOriginalState(JSON.stringify(songToSave))
//     onSongsChanged(songToSave, songToSave.id)
//   }

//   // Nota: Eliminar se gestiona en el padre normalmente, pero dejamos el botón local por si acaso
//   const handleDeleteSong = () => {
//     if (!window.confirm("¿Borrar canción? Esta acción no se puede deshacer.")) return
//     const nextSongs = songs.filter((s) => s.id !== draft.id)
//     onSongsChanged(nextSongs, null)
//   }

//   // -- IMPORT / EXPORT --
//   const copyJson = () => {
//       navigator.clipboard.writeText(JSON.stringify(draft, null, 2))
//         .then(() => showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'JSON copiado al portapapeles' }))
//   }

//   const importJson = async () => {
//     try {
//       const text = await navigator.clipboard.readText()
//       const obj = JSON.parse(text)
//       if (!Array.isArray(obj.lyrics)) throw new Error()
      
//       const imported = { ...obj, id: generateUuid(), name: obj.name + " (Importada)" }
//       setDraft(imported)
//       showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Canción importada (Recuerda guardar)' })
//     } catch {
//       showMessage({ type: MESSAGE_TYPE.BAD, text: 'JSON inválido' })
//     }
//   }

//   // -- GESTIÓN DE BLOQUES (CRUD INTERNO) --
//   const resetBlockForm = () => {
//     setBlockForm({ type: "lyric", mode: "uniform", text: "", durationMs: 3000, display: "icon", parts: [] })
//     setEditingBlockId(null)
//   }

//   const loadBlockToEdit = (block) => {
//     setBlockForm({
//       type: block.type || "lyric",
//       mode: block.mode || "uniform",
//       text: block.text || "",
//       durationMs: block.durationMs || 2000,
//       display: block.display || "icon",
//       parts: block.parts ? JSON.parse(JSON.stringify(block.parts)) : [],
//     })
//     setEditingBlockId(block.id)
//   }

//   const saveBlock = () => {
//     const newBlock = { id: editingBlockId || generateUuid(), type: blockForm.type, durationMs: Number(blockForm.durationMs) }

//     if (blockForm.type === "lyric") {
//       newBlock.mode = blockForm.mode
//       if (blockForm.mode === "uniform") {
//         newBlock.text = blockForm.text
//       } else {
//         if (blockForm.parts.length === 0) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Debes agregar al menos una parte' })
//         const totalPartsMs = blockForm.parts.reduce((acc, p) => acc + (Number(p.durationMs) || 0), 0)
//         newBlock.parts = blockForm.parts
//         newBlock.durationMs = totalPartsMs
//       }
//     } else {
//       newBlock.display = blockForm.display
//     }

//     let newLyrics = [...draft.lyrics]
//     if (editingBlockId) {
//       const idx = newLyrics.findIndex((b) => b.id === editingBlockId)
//       if (idx >= 0) newLyrics[idx] = newBlock
//     } else {
//       newLyrics.push(newBlock)
//     }

//     setDraft((prev) => ({ ...prev, lyrics: newLyrics }))
    
//     // MENSAJE DE ÉXITO AL GUARDAR BLOQUE
//     showMessage({ 
//         type: MESSAGE_TYPE.SUCCESS, 
//         text: editingBlockId ? 'Bloque actualizado correctamente' : 'Bloque agregado correctamente' 
//     })

//     resetBlockForm()
//   }

//   const removeBlock = (blockId) => {
//     setDraft((prev) => ({ ...prev, lyrics: prev.lyrics.filter((b) => b.id !== blockId) }))
//     if (editingBlockId === blockId) resetBlockForm()
//   }

//   const moveBlock = (index, direction) => {
//     const newLyrics = [...draft.lyrics]
//     if (direction === -1 && index > 0) [newLyrics[index], newLyrics[index - 1]] = [newLyrics[index - 1], newLyrics[index]]
//     else if (direction === 1 && index < newLyrics.length - 1) [newLyrics[index], newLyrics[index + 1]] = [newLyrics[index + 1], newLyrics[index]]
//     setDraft((prev) => ({ ...prev, lyrics: newLyrics }))
//   }

//   // -- HERRAMIENTAS TAP --
//   const toggleTiming = () => {
//     if (!isTiming) {
//       setIsTiming(true)
//       setTimingStart(performance.now())
//     } else {
//       const diff = Math.round(performance.now() - timingStart)
//       setIsTiming(false)
//       setBlockForm(prev => ({ ...prev, durationMs: diff }))
//     }
//   }

//   const startMeasuringPart = (index) => {
//     setMeasuringPartIndex(index)
//     setTimingStart(performance.now())
//   }

//   const stopMeasuringPart = (index) => {
//     if (measuringPartIndex !== index) return
//     const diff = Math.round(performance.now() - timingStart)
//     const newParts = [...blockForm.parts]
//     newParts[index].durationMs = diff
//     setBlockForm(prev => ({ ...prev, parts: newParts }))
//     setMeasuringPartIndex(null)
//   }

//   // -- RENDER HELPERS --
//   const renderBlockItem = (block, index) => {
//     let title = block.type === "instrumental" ? `🎵 INSTRUMENTAL (${block.display})` : (block.mode === "uniform" ? block.text : `[PARTS] ${block.parts?.map(p => p.text).join("")}`)
//     const isEditing = editingBlockId === block.id

//     return (
//       <div key={block.id} style={{ ...styles.blockItem, ...(isEditing ? styles.activeBlock : {}) }}>
//         <div style={{ fontWeight: "bold", color: "#888", width: 20 }}>{index + 1}.</div>
//         <div style={{ flex: 1, overflow: "hidden" }} onClick={() => loadBlockToEdit(block)}>
//           <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title || "(Vacío)"}</div>
//           <div style={{ fontSize: 11, color: "#666" }}>{block.durationMs}ms</div>
//         </div>
//         <div style={{ display: "flex", flexDirection: "column" }}>
//           <button onClick={() => moveBlock(index, -1)} style={{ fontSize: 10, cursor: "pointer", border: "none", background: "none" }}>▲</button>
//           <button onClick={() => moveBlock(index, 1)} style={{ fontSize: 10, cursor: "pointer", border: "none", background: "none" }}>▼</button>
//         </div>
//         <button onClick={(e) => { e.stopPropagation(); removeBlock(block.id); }} style={{ ...styles.btn, ...styles.btnDanger, padding: "2px 6px", fontSize: 10 }}>X</button>
//       </div>
//     )
//   }

//   return (
//     <div style={styles.container}>
//         {/* CABECERA */}
//         <div className='LyricsGenerator__Editor__MetadataSong Element--Shadow'>
//             <div className='LyricsGenerator__Editor__MetadataSong__Header'>
//                 <div style={{display:'flex', alignItems:'center', flexWrap:'wrap', gap: 10}}>
//                     {isNewSong ? (
//                         <span className='LyricsGenerator__Text--Title'><i className='bi bi-plus-circle'></i> Nueva canción</span>
//                     ) : (
//                         <span className='LyricsGenerator__Text--Title'><i className='bi bi-pencil'></i> Editando canción</span>
//                     )}

//                     {/* INDICADORES DE ESTADO */}
//                     {hasUnsavedChanges && (
//                         <span style={{...styles.statusBadge, background:'#fff3cd', color:'#856404', border:'1px solid #ffeeba'}}>
//                             <i className="bi bi-exclamation-triangle-fill"></i> Cambios sin guardar
//                         </span>
//                     )}
//                     {!hasUnsavedChanges && draft.updatedAt && (
//                         <span style={{...styles.statusBadge, color:'#888', fontWeight:'normal'}}>
//                             Guardado: {new Date(draft.updatedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
//                         </span>
//                     )}
//                 </div>

//                 <div className='LyricsGenerator__Editor__MetadataSong__Buttons'>
//                     <button className='Btn__Adsl--Red LyricsGenerator__Editor__MetadataSong__Buttons__Button' onClick={handleSaveSong}><i className='bi bi-floppy'></i> Guardar</button>
//                     {/* Botones con modal de confirmación */}
//                     <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Editor__MetadataSong__Buttons__Button' onClick={requestDuplicate}><i className='bi bi-files'></i> Duplicar</button>
//                     <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Editor__MetadataSong__Buttons__Button' onClick={requestClean}><i className='bi bi-eraser'></i> Limpiar</button>
//                 </div>
//             </div>

//             <div style={styles.row}>
//                 <div style={{ flex: 1 }}><label style={styles.label}>Nombre</label><input style={styles.input} value={draft.name} onChange={(e) => updateDraftField("name", e.target.value)} /></div>
//                 <div style={{ flex: 1 }}><label style={styles.label}>Artista</label><input style={styles.input} value={draft.artist} onChange={(e) => updateDraftField("artist", e.target.value)} /></div>
//                 <div style={{ flex: 1 }}><label style={styles.label}>URL</label><input style={styles.input} value={draft.url} onChange={(e) => updateDraftField("url", e.target.value)} /></div>
//             </div>

//             {/* BARRA DE UTILIDADES JSON */}
//             <div style={{ marginTop: 10, display: "flex", gap: 15, alignItems: "center" }}>
//                 <button onClick={copyJson} style={{ fontSize: 12, background: "none", border: "none", color: "#007bff", cursor: "pointer", textDecoration: "underline" }}><i className='bi bi-copy'></i> Copiar JSON</button>
//                 <button onClick={importJson} style={{ fontSize: 12, background: "none", border: "none", color: "#007bff", cursor: "pointer", textDecoration: "underline" }}><i className='bi bi-clipboard2'></i> Pegar JSON</button>
                
//                 {!isNewSong && (
//                     <button onClick={handleDeleteSong} style={{ fontSize: 12, background: "none", border: "none", color: "red", cursor: "pointer", marginLeft: "auto", textDecoration: "underline" }}><i className='bi bi-trash'></i> Eliminar</button>
//                 )}
//             </div>
//       </div>

//       {/* WORKSPACE */}
//       <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
//         {/* LISTA IZQ */}
//         <div>
//           <label style={{ ...styles.label, marginBottom: 8 }}>Línea de Tiempo ({draft.lyrics.length} bloques)</label>
//           <div style={styles.blockList}>
//             {draft.lyrics.length === 0 ? <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Sin bloques</div> : draft.lyrics.map(renderBlockItem)}
//           </div>
//         </div>

//         {/* EDITOR DER */}
//         <div style={styles.card}>
//             <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
//                 <p>
//                     {editingBlockId ? (
//                         <span><i className='bi bi-pencil'></i> Editar bloque</span>
//                     ) : (
//                         <span><i className='bi bi-plus-lg'></i> Nuevo bloque</span>
//                     )}
//                 </p>
//                 {editingBlockId && <button className='Btn__Adsl--RedOutlineTransparent' onClick={resetBlockForm}><i className='bi bi-x-lg'></i> Cancelar</button>}
//             </div>

//             <div style={{ marginBottom: 12 }}>
//                 <label style={styles.label}>Tipo</label>
//                 <div style={{ display: "flex", gap: 15 }}>
//                 <label style={{ cursor: "pointer" }}><input type="radio" checked={blockForm.type === "lyric"} onChange={() => setBlockForm(p => ({ ...p, type: "lyric" }))} /> Letra</label>
//                 <label style={{ cursor: "pointer" }}><input type="radio" checked={blockForm.type === "instrumental"} onChange={() => setBlockForm(p => ({ ...p, type: "instrumental" }))} /> Instrumental</label>
//                 </div>
//             </div>

//             {/* FORMULARIO */}
//             {blockForm.type === "instrumental" ? (
//                 <div style={{ marginBottom: 12 }}>
//                 <label style={styles.label}>Mostrar Ícono</label>
//                 <select style={styles.input} value={blockForm.display} onChange={(e) => setBlockForm(p => ({ ...p, display: e.target.value }))}>
//                     <option value="icon">Sí (♪)</option>
//                     <option value="none">No (Vacío)</option>
//                 </select>
//                 </div>
//             ) : (
//                 <>
//                 <div style={{ marginBottom: 12 }}>
//                     <label style={styles.label}>Modo</label>
//                     <select style={styles.input} value={blockForm.mode} onChange={(e) => setBlockForm(p => ({ ...p, mode: e.target.value }))}>
//                     <option value="uniform">Uniforme (Línea completa)</option>
//                     <option value="parts">Por Partes (Silábico)</option>
//                     </select>
//                 </div>

//                 {blockForm.mode === "uniform" ? (
//                     <div style={{ marginBottom: 12 }}>
//                     <label style={styles.label}>Texto</label>
//                     <input style={styles.input} placeholder="Ej. Tal vez me olvide..." value={blockForm.text} onChange={(e) => setBlockForm(p => ({ ...p, text: e.target.value }))} autoFocus />
//                     </div>
//                 ) : (
//                     <div style={{ marginBottom: 12, background: "#fdfdfd", padding: 8, border: "1px dashed #ccc", borderRadius: 4 }}>
//                     <label style={styles.label}>Partes (Texto + Duración)</label>
//                     {blockForm.parts.map((part, idx) => (
//                         <div key={idx} style={{ display: "flex", gap: 5, marginBottom: 5, alignItems: "center" }}>
//                         <input style={{ ...styles.input, flex: 2 }} placeholder="Sílaba" value={part.text} onChange={(e) => { const n = [...blockForm.parts]; n[idx].text = e.target.value; setBlockForm(p => ({ ...p, parts: n })); }} />
//                         <input style={{ ...styles.input, width: 60 }} type="number" value={part.durationMs} onChange={(e) => { const n = [...blockForm.parts]; n[idx].durationMs = Number(e.target.value); setBlockForm(p => ({ ...p, parts: n })); }} />
//                         <button
//                             onMouseDown={() => startMeasuringPart(idx)} onMouseUp={() => stopMeasuringPart(idx)}
//                             onTouchStart={() => startMeasuringPart(idx)} onTouchEnd={() => stopMeasuringPart(idx)}
//                             style={{ ...styles.btn, padding: "0 8px", height: "36px", background: measuringPartIndex === idx ? "#ffeb3b" : "#eee", border: measuringPartIndex === idx ? "1px solid #d4b600" : "1px solid #ccc" }}
//                             title="Mantén para medir">
//                             {measuringPartIndex === idx ? "⏱" : "●"}
//                         </button>
//                         <button onClick={() => setBlockForm(p => ({ ...p, parts: p.parts.filter((_, i) => i !== idx) }))} style={{ ...styles.btn, ...styles.btnDanger, padding: "0 8px", height: "36px" }}>x</button>
//                         </div>
//                     ))}
//                     <button onClick={() => setBlockForm(p => ({ ...p, parts: [...p.parts, { text: "", durationMs: 1000 }] }))} style={{ fontSize: 12, marginTop: 5, cursor: "pointer", background: "none", border: "none", color: "#007bff" }}>+ Agregar Parte</button>
//                     </div>
//                 )}
//                 </>
//             )}

//             {/* DURACIÓN */}
//             {(blockForm.type === "instrumental" || blockForm.mode === "uniform") && (
//                 <div style={{ marginBottom: 12 }}>
//                 <label style={styles.label}>Duración Total (ms)</label>
//                 <div style={{ display: "flex", gap: 8 }}>
//                     <input type="number" style={styles.input} value={blockForm.durationMs} onChange={(e) => setBlockForm(p => ({ ...p, durationMs: Number(e.target.value) }))} />
//                     <button
//                     onMouseDown={toggleTiming} onMouseUp={toggleTiming}
//                     onTouchStart={toggleTiming} onTouchEnd={toggleTiming}
//                     style={{ ...styles.btn, ...styles.tapBtn, background: isTiming ? "#ffeb3b" : "#eee", color: isTiming ? "#000" : "#333" }}>
//                     {isTiming ? "■ SOLTAR" : "● MEDIR"}
//                     </button>
//                 </div>
//                 </div>
//             )}

//             <button className='Btn__Adsl--Red' onClick={saveBlock}>
//                 {editingBlockId ? <span><i className='bi bi-floppy'></i> Guardar cambio de bloque</span> : <span><i className='bi bi-plus-circle'></i> Agregar bloque</span>}
//             </button>
//         </div>
//       </div>

//       <button className='Btn__Adsl--Red mx-auto w-100 mt-5' onClick={handleSaveSong}><i className='bi bi-floppy'></i> Guardar canción</button>
//       <p className='text-center'>
//         {/* INDICADORES DE ESTADO */}
//         {hasUnsavedChanges && (
//             <span style={{...styles.statusBadge, background:'#fff3cd', color:'#856404', border:'1px solid #ffeeba'}}>
//                 <i className="bi bi-exclamation-triangle-fill"></i> Cambios sin guardar
//             </span>
//         )}
//       </p>
//       <p className='text-center'>
//         {!hasUnsavedChanges && draft.updatedAt && (
//             <span style={{...styles.statusBadge, color:'#888', fontWeight:'normal'}}>
//                 Guardado: {new Date(draft.updatedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
//             </span>
//         )}
//       </p>

//       {/* MODAL DE CONFIRMACIÓN */}
//       {showConfirm && (
//           <Confirm 
//              message={confirmConfig?.message}
//              textYesButton={confirmConfig?.textYesButton}
//              textNoButton={confirmConfig?.textNoButton}
//              actionToYesButton={confirmConfig?.actionToYesButton}
//              setShowModal={setShowConfirm}
//              setConfirmData={setConfirmConfig}
//           />
//       )}
//     </div>
//   )
// }

// export { KaraokeSongCreator };





























// Funcional
// /**
//  * ==============================================================================
//  * ESTILOS INLINE (Para portabilidad rápida, idealmente mover a CSS/SASS)
//  * ==============================================================================
//  */
// const styles = {
//   container: { display: "flex", flexDirection: "column", gap: 20 },
//   row: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
//   card: { background: "#fff", border: "1px solid #ddd", borderRadius: 8, padding: 16, boxShadow: "0 2px 5px rgba(0,0,0,0.05)" },
//   label: { fontWeight: 700, fontSize: "0.85rem", color: "#555", display: "block", marginBottom: 4 },
//   input: { padding: 8, borderRadius: 4, border: "1px solid #ccc", width: "100%" },
//   btn: { padding: "8px 16px", borderRadius: 4, border: "none", background: "#eee", cursor: "pointer", fontWeight: 600 },
//   btnPrimary: { background: "#007bff", color: "#fff" },
//   btnDanger: { background: "#dc3545", color: "#fff" },
//   blockList: { display: "flex", flexDirection: "column", gap: 8, maxHeight: "500px", overflowY: "auto", border: "1px solid #eee", padding: 8, borderRadius: 4 },
//   blockItem: { display: "flex", alignItems: "center", gap: 10, background: "#f9f9f9", border: "1px solid #eee", padding: 8, borderRadius: 4, cursor: "pointer" },
//   activeBlock: { borderLeft: "4px solid #007bff", background: "#f0f8ff" },
//   tapBtn: { minWidth: "80px", border: "1px solid #bbb", fontWeight: "bold" },
// };

// /**
//  * ==============================================================================
//  * COMPONENTE: KaraokeSongCreator
//  * ------------------------------------------------------------------------------
//  * Editor CRUD para canciones. Permite "grabar" tiempos manualmente (Tap-to-Sync).
//  * ==============================================================================
//  */
// const KaraokeSongCreator = ({ songs, selectedSongId, onSongsChanged, onRequestClose, generateUuid }) => {
  
//   // -- ESTADO: Draft (Borrador de la canción actual) --
//   const [draft, setDraft] = useState({ id: "", name: "", artist: "", url: "", lyrics: [] });
//   const [isNewSong, setIsNewSong] = useState(false);
//   const [editingBlockId, setEditingBlockId] = useState(null);

//   // -- ESTADO: Formulario de Bloque (Lo que se está editando en el panel derecho) --
//   const [blockForm, setBlockForm] = useState({
//     type: "lyric",       // "lyric" | "instrumental"
//     mode: "uniform",     // "uniform" (línea completa) | "parts" (sílaba a sílaba)
//     text: "",            // Texto para modo uniforme
//     durationMs: 3000,    // Duración para modo uniforme o instrumental
//     display: "icon",     // "none" | "icon" (solo para instrumental)
//     parts: [],           // Array [{text, durationMs}, ...] para modo parts
//   });

//   // -- ESTADO: Herramientas de Medición (Tap to Sync) --
//   const [isTiming, setIsTiming] = useState(false);         // Para medir bloque completo
//   const [timingStart, setTimingStart] = useState(0);       // Marca de tiempo inicio
//   const [measuringPartIndex, setMeasuringPartIndex] = useState(null); // Índice de la parte que se está midiendo

  
//   const { showMessage } = useFloatMessage()

//   /**
//    * Carga inicial: Si hay ID seleccionado, clona la canción para editarla sin afectar la original.
//    * Si no, prepara un draft limpio.
//    */
//   useEffect(() => {
//     const current = songs.find((s) => s.id === selectedSongId);
//     if (current) {
//       setDraft(JSON.parse(JSON.stringify(current))); // Deep copy para evitar mutación por referencia
//       setIsNewSong(false);
//     } else {
//       createNewDraft();
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [selectedSongId, songs]);

//   // -- GESTIÓN DEL DRAFT (METADATA) --
//   const createNewDraft = () => {
//     setDraft({ id: generateUuid(), name: "Nueva Canción", artist: "Artista Desconocido", url: "", lyrics: [], createdAt: new Date().toISOString() });
//     setIsNewSong(true);
//     setEditingBlockId(null);
//   };

//   const updateDraftField = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));

//   const handleSaveSong = async () => {
//     // 1. Validaciones básicas
//     if (!draft.name.trim()) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'La canción necesita un nombre' })
    
//     // 2. Preparamos el objeto para enviar a la API
//     // Nota: draft.lyrics es el array de bloques. La API lo espera en la propiedad "Lyrics".
//     const payload = {
//       Id: draft.id,
//       Name: draft.name,
//       Artist: draft.artist,
//       Url: draft.url,
//       Lyrics: draft.lyrics 
//     };

//     try {
//         // 3. Enviamos a la API (Ajusta la URL si tu puerto es diferente)
//         // Usamos fetch, pero si usas axios cámbialo a axios.post
//         const response = await fetch(`${process.env.REACT_APP_API_URL}karaoke/songs`, {
//             method: 'POST',
//             headers: { 'Content-Type': 'application/json' },
//             body: JSON.stringify(payload)
//         });

//         if (response.ok) {
//             showMessage({
//                 type: MESSAGE_TYPE.SUCCESS,
//                 text: 'Canción guardada con éxito'
//             })
//             // Opcional: Redirigir al dashboard o limpiar
//             // navigate('/admin/karaoke'); 
//         } else {
//             showMessage({
//                 type: MESSAGE_TYPE.BAD,
//                 text: 'Ha habido un error al guardar la canción. Inténtalo nuevamente o inténtalo más tarde'
//             })
//             console.error(await response.text());
//         }
//     } catch (error) {
//         console.error("Error de red:", error);
//         showMessage({
//             type: MESSAGE_TYPE.BAD,
//             text: 'No fue posible conectar con el servidor. Inténtalo nuevamente o inténtalo más tarde.'
//         })
//     }
//   };

//   const handleDeleteSong = () => {
//     if (!window.confirm("¿Borrar canción? Esta acción no se puede deshacer.")) return;
//     const nextSongs = songs.filter((s) => s.id !== draft.id);
//     onSongsChanged(nextSongs, null);
//   };

//   const handleDuplicateSong = () => {
//     const copy = { ...draft, id: generateUuid(), name: `${draft.name} (Copia)`, createdAt: new Date().toISOString() };
//     onSongsChanged([...songs, copy], copy.id);
//   };

//   // -- GESTIÓN DE BLOQUES (CRUD INTERNO) --

//   /** Reinicia el formulario para agregar un bloque nuevo */
//   const resetBlockForm = () => {
//     setBlockForm({ type: "lyric", mode: "uniform", text: "", durationMs: 3000, display: "icon", parts: [] });
//     setEditingBlockId(null);
//   };

//   /** Carga un bloque existente al formulario */
//   const loadBlockToEdit = (block) => {
//     setBlockForm({
//       type: block.type || "lyric",
//       mode: block.mode || "uniform",
//       text: block.text || "",
//       durationMs: block.durationMs || 2000,
//       display: block.display || "icon",
//       parts: block.parts ? JSON.parse(JSON.stringify(block.parts)) : [],
//     });
//     setEditingBlockId(block.id);
//   };

//   /** Guarda (agrega o actualiza) el bloque del formulario en el array de lyrics */
//   const saveBlock = () => {
//     const newBlock = { id: editingBlockId || generateUuid(), type: blockForm.type, durationMs: Number(blockForm.durationMs) };

//     if (blockForm.type === "lyric") {
//       newBlock.mode = blockForm.mode;
//       if (blockForm.mode === "uniform") {
//         newBlock.text = blockForm.text;
//       } else {
//         // Validación para modo parts
//         if (blockForm.parts.length === 0) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Debes agregar al menos una parte' })
//         // En modo parts, la duración total es la suma de las partes
//         const totalPartsMs = blockForm.parts.reduce((acc, p) => acc + (Number(p.durationMs) || 0), 0);
//         newBlock.parts = blockForm.parts;
//         newBlock.durationMs = totalPartsMs;
//       }
//     } else {
//       newBlock.display = blockForm.display; // Configuración extra para instrumental
//     }

//     let newLyrics = [...draft.lyrics];
//     if (editingBlockId) {
//       const idx = newLyrics.findIndex((b) => b.id === editingBlockId);
//       if (idx >= 0) newLyrics[idx] = newBlock;
//     } else {
//       newLyrics.push(newBlock);
//     }

//     setDraft((prev) => ({ ...prev, lyrics: newLyrics }));
//     resetBlockForm();
//   };

//   const removeBlock = (blockId) => {
//     setDraft((prev) => ({ ...prev, lyrics: prev.lyrics.filter((b) => b.id !== blockId) }));
//     if (editingBlockId === blockId) resetBlockForm();
//   };

//   const moveBlock = (index, direction) => {
//     const newLyrics = [...draft.lyrics];
//     if (direction === -1 && index > 0) [newLyrics[index], newLyrics[index - 1]] = [newLyrics[index - 1], newLyrics[index]];
//     else if (direction === 1 && index < newLyrics.length - 1) [newLyrics[index], newLyrics[index + 1]] = [newLyrics[index + 1], newLyrics[index]];
//     setDraft((prev) => ({ ...prev, lyrics: newLyrics }));
//   };

//   // -- HERRAMIENTAS: TAP TO SYNC (MEDICIÓN MANUAL) --
  
//   /** Mide duración general (para Uniform o Instrumental) */
//   const toggleTiming = () => {
//     if (!isTiming) {
//       setIsTiming(true);
//       setTimingStart(performance.now());
//     } else {
//       const diff = Math.round(performance.now() - timingStart);
//       setIsTiming(false);
//       setBlockForm(prev => ({ ...prev, durationMs: diff }));
//     }
//   };

//   /** Inicia medición de una parte específica (Sílaba) */
//   const startMeasuringPart = (index) => {
//     setMeasuringPartIndex(index);
//     setTimingStart(performance.now());
//   };

//   /** Detiene medición de una parte específica y actualiza solo ese item */
//   const stopMeasuringPart = (index) => {
//     if (measuringPartIndex !== index) return;
//     const diff = Math.round(performance.now() - timingStart);
    
//     const newParts = [...blockForm.parts];
//     newParts[index].durationMs = diff;
//     setBlockForm(prev => ({ ...prev, parts: newParts }));
//     setMeasuringPartIndex(null);
//   };

//   // -- IMPORT / EXPORT --
//   const copyJson = () => navigator.clipboard.writeText(JSON.stringify(draft, null, 2)).then(() => showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Se ha copiado exitosamente el JSON al portapapeles' }));
//   const importJson = async () => {
//     try {
//       const text = await navigator.clipboard.readText();
//       const obj = JSON.parse(text);
//       if (!Array.isArray(obj.lyrics)) throw new Error();
//       setDraft({ ...obj, id: generateUuid(), name: obj.name + " (Importada)" }); // Nuevo ID para evitar conflictos
//       showMessage({
//           type: MESSAGE_TYPE.SUCCESS,
//           text: 'Se ha importado exitosamente la canción'
//       })
//     } catch {
//       showMessage({
//           type: MESSAGE_TYPE.BAD,
//           text: 'El JSON que ingresaste no es válido'
//       })
//     }
//   };

//   // -- RENDER HELPERS --
//   const renderBlockItem = (block, index) => {
//     // Generamos un título descriptivo según el tipo
//     let title = block.type === "instrumental" ? `🎵 INSTRUMENTAL (${block.display})` : (block.mode === "uniform" ? block.text : `[PARTS] ${block.parts?.map(p => p.text).join("")}`);
//     const isEditing = editingBlockId === block.id;

//     return (
//       <div key={block.id} style={{ ...styles.blockItem, ...(isEditing ? styles.activeBlock : {}) }}>
//         <div style={{ fontWeight: "bold", color: "#888", width: 20 }}>{index + 1}.</div>
//         <div style={{ flex: 1, overflow: "hidden" }} onClick={() => loadBlockToEdit(block)}>
//           <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title || "(Vacío)"}</div>
//           <div style={{ fontSize: 11, color: "#666" }}>{block.durationMs}ms</div>
//         </div>
//         <div style={{ display: "flex", flexDirection: "column" }}>
//           <button onClick={() => moveBlock(index, -1)} style={{ fontSize: 10, cursor: "pointer", border: "none", background: "none" }}>▲</button>
//           <button onClick={() => moveBlock(index, 1)} style={{ fontSize: 10, cursor: "pointer", border: "none", background: "none" }}>▼</button>
//         </div>
//         <button onClick={(e) => { e.stopPropagation(); removeBlock(block.id); }} style={{ ...styles.btn, ...styles.btnDanger, padding: "2px 6px", fontSize: 10 }}>X</button>
//       </div>
//     );
//   };

//   // -- RENDER PRINCIPAL --
//   return (
//     <div style={styles.container}>
//         {/* 1. CABECERA (METADATA) */}
//         <div className='LyricsGenerator__Editor__MetadataSong Element--Shadow'> {/* style={styles.card} */}
//             <div className='LyricsGenerator__Editor__MetadataSong__Header'>
//                 <p>
//                     {isNewSong ? (
//                         <span className='LyricsGenerator__Text--Title'><i className='bi bi-plus-circle'></i> Nueva canción</span>
//                     ) : (
//                         <span className='LyricsGenerator__Text--Title'><i className='bi bi-pencil'></i> Editando canción</span>
//                     )}
//                 </p>

//                 <div className='LyricsGenerator__Editor__MetadataSong__Buttons'>
//                     <button className='Btn__Adsl--Red LyricsGenerator__Editor__MetadataSong__Buttons__Button' onClick={handleSaveSong}><i className='bi bi-floppy'></i> Guardar</button>
//                     <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Editor__MetadataSong__Buttons__Button' onClick={handleDuplicateSong}><i className='bi bi-files'></i> Duplicar</button>
//                     <button className='Btn__Adsl--RedOutlineTransparent LyricsGenerator__Editor__MetadataSong__Buttons__Button' onClick={createNewDraft}><i className='bi bi-eraser'></i> Limpiar</button>
//                 </div>
//             </div>

//             <div style={styles.row}>
//                 <div style={{ flex: 1 }}><label style={styles.label}>Nombre</label><input style={styles.input} value={draft.name} onChange={(e) => updateDraftField("name", e.target.value)} /></div>
//                 <div style={{ flex: 1 }}><label style={styles.label}>Artista</label><input style={styles.input} value={draft.artist} onChange={(e) => updateDraftField("artist", e.target.value)} /></div>
//                 <div style={{ flex: 1 }}><label style={styles.label}>URL</label><input style={styles.input} value={draft.url} onChange={(e) => updateDraftField("url", e.target.value)} /></div>
//             </div>

//             <div style={{ marginTop: 10, display: "flex", gap: 15 }}>
//                 <button onClick={copyJson} style={{ fontSize: 12, background: "none", border: "none", color: "#007bff", cursor: "pointer", textDecoration: "underline" }}><i className='bi bi-copy'></i> Copiar JSON</button>
//                 <button onClick={importJson} style={{ fontSize: 12, background: "none", border: "none", color: "#007bff", cursor: "pointer", textDecoration: "underline" }}><i className='bi bi-clipboard2'></i> Pegar JSON</button>
//                 {!isNewSong && <button onClick={handleDeleteSong} style={{ fontSize: 12, background: "none", border: "none", color: "red", cursor: "pointer", marginLeft: "auto", textDecoration: "underline" }}><i className='bi bi-trash'></i> Eliminar</button>}
//             </div>
//       </div>

//       {/* 2. AREA DE TRABAJO (2 COLUMNAS) */}
//       <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
//         {/* COLUMNA IZQ: LISTA (TIMELINE) */}
//         <div>
//           <label style={{ ...styles.label, marginBottom: 8 }}>Línea de Tiempo ({draft.lyrics.length} bloques)</label>
//           <div style={styles.blockList}>
//             {draft.lyrics.length === 0 ? <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Sin bloques</div> : draft.lyrics.map(renderBlockItem)}
//           </div>
//         </div>

//         {/* COLUMNA DER: FORMULARIO */}
//         <div style={styles.card}>
//             <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
//                 <p>
//                     {editingBlockId ? (
//                         <span>
//                             <i className='bi bi-pencil'></i> Editar bloque
//                         </span>
//                     ) : (
//                         <span>
//                             <i className='bi bi-plus-lg'></i> Nuevo bloque
//                         </span>
//                     )}
//                 </p>

//                 {editingBlockId && <button className='Btn__Adsl--RedOutlineTransparent' onClick={resetBlockForm}><i className='bi bi-x-lg'></i> Cancelar</button>}
//             </div>

//             <div style={{ marginBottom: 12 }}>
//                 <label style={styles.label}>Tipo</label>
//                 <div style={{ display: "flex", gap: 15 }}>
//                 <label style={{ cursor: "pointer" }}><input type="radio" checked={blockForm.type === "lyric"} onChange={() => setBlockForm(p => ({ ...p, type: "lyric" }))} /> Letra</label>
//                 <label style={{ cursor: "pointer" }}><input type="radio" checked={blockForm.type === "instrumental"} onChange={() => setBlockForm(p => ({ ...p, type: "instrumental" }))} /> Instrumental</label>
//                 </div>
//             </div>

//             {/* CAMPOS ESPECÍFICOS SEGÚN TIPO */}
//             {blockForm.type === "instrumental" ? (
//                 <div style={{ marginBottom: 12 }}>
//                 <label style={styles.label}>Mostrar Ícono</label>
//                 <select style={styles.input} value={blockForm.display} onChange={(e) => setBlockForm(p => ({ ...p, display: e.target.value }))}>
//                     <option value="icon">Sí (♪)</option>
//                     <option value="none">No (Vacío)</option>
//                 </select>
//                 </div>
//             ) : (
//                 <>
//                 <div style={{ marginBottom: 12 }}>
//                     <label style={styles.label}>Modo</label>
//                     <select style={styles.input} value={blockForm.mode} onChange={(e) => setBlockForm(p => ({ ...p, mode: e.target.value }))}>
//                     <option value="uniform">Uniforme (Línea completa)</option>
//                     <option value="parts">Por Partes (Silábico)</option>
//                     </select>
//                 </div>

//                 {blockForm.mode === "uniform" ? (
//                     <div style={{ marginBottom: 12 }}>
//                     <label style={styles.label}>Texto</label>
//                     <input style={styles.input} placeholder="Ej. Tal vez me olvide..." value={blockForm.text} onChange={(e) => setBlockForm(p => ({ ...p, text: e.target.value }))} autoFocus />
//                     </div>
//                 ) : (
//                     /* MODO PARTES CON TAP TO SYNC INDIVIDUAL */
//                     <div style={{ marginBottom: 12, background: "#fdfdfd", padding: 8, border: "1px dashed #ccc", borderRadius: 4 }}>
//                     <label style={styles.label}>Partes (Texto + Duración)</label>
//                     {blockForm.parts.map((part, idx) => (
//                         <div key={idx} style={{ display: "flex", gap: 5, marginBottom: 5, alignItems: "center" }}>
//                         <input style={{ ...styles.input, flex: 2 }} placeholder="Sílaba" value={part.text} onChange={(e) => { const n = [...blockForm.parts]; n[idx].text = e.target.value; setBlockForm(p => ({ ...p, parts: n })); }} />
//                         <input style={{ ...styles.input, width: 60 }} type="number" value={part.durationMs} onChange={(e) => { const n = [...blockForm.parts]; n[idx].durationMs = Number(e.target.value); setBlockForm(p => ({ ...p, parts: n })); }} />
//                         <button
//                             onMouseDown={() => startMeasuringPart(idx)} onMouseUp={() => stopMeasuringPart(idx)}
//                             onTouchStart={() => startMeasuringPart(idx)} onTouchEnd={() => stopMeasuringPart(idx)}
//                             style={{ ...styles.btn, padding: "0 8px", height: "36px", background: measuringPartIndex === idx ? "#ffeb3b" : "#eee", border: measuringPartIndex === idx ? "1px solid #d4b600" : "1px solid #ccc" }}
//                             title="Mantén para medir">
//                             {measuringPartIndex === idx ? "⏱" : "●"}
//                         </button>
//                         <button onClick={() => setBlockForm(p => ({ ...p, parts: p.parts.filter((_, i) => i !== idx) }))} style={{ ...styles.btn, ...styles.btnDanger, padding: "0 8px", height: "36px" }}>x</button>
//                         </div>
//                     ))}
//                     <button onClick={() => setBlockForm(p => ({ ...p, parts: [...p.parts, { text: "", durationMs: 1000 }] }))} style={{ fontSize: 12, marginTop: 5, cursor: "pointer", background: "none", border: "none", color: "#007bff" }}>+ Agregar Parte</button>
//                     </div>
//                 )}
//                 </>
//             )}

//             {/* DURACIÓN GENERAL (Solo para Uniform o Instrumental) */}
//             {(blockForm.type === "instrumental" || blockForm.mode === "uniform") && (
//                 <div style={{ marginBottom: 12 }}>
//                 <label style={styles.label}>Duración Total (ms)</label>
//                 <div style={{ display: "flex", gap: 8 }}>
//                     <input type="number" style={styles.input} value={blockForm.durationMs} onChange={(e) => setBlockForm(p => ({ ...p, durationMs: Number(e.target.value) }))} />
//                     <button
//                     onMouseDown={toggleTiming} onMouseUp={toggleTiming}
//                     onTouchStart={toggleTiming} onTouchEnd={toggleTiming}
//                     style={{ ...styles.btn, ...styles.tapBtn, background: isTiming ? "#ffeb3b" : "#eee", color: isTiming ? "#000" : "#333" }}>
//                     {isTiming ? "■ SOLTAR" : "● MEDIR"}
//                     </button>
//                 </div>
//                 </div>
//             )}

//             <button className='Btn__Adsl--Red' onClick={saveBlock}>
//                 {editingBlockId ? (
//                     <span>
//                         <i className='bi bi-floppy'></i> Guardar cambio de bloque
//                     </span>
//                 ) : (
//                     <span>
//                         <i className='bi bi-plus-circle'></i> Agregar bloque
//                     </span>
//                 )}
//             </button>
//         </div>

//       </div>

//         <button className='Btn__Adsl--Red mx-auto w-100 mt-5' onClick={handleSaveSong}><i className='bi bi-floppy'></i> Guardar canción</button>
//     </div>
//   );
// };

// export { KaraokeSongCreator };